import type { RefObject } from 'react'
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from 'motion/react'
import { motionTokens } from '../../motion/motionTokens'
import type { BucketStage as BucketStageId } from './bucketReducer'

const STAGE_PROGRESS: Readonly<Record<BucketStageId, number>> = {
  eligibility: 1,
  recipient: 2,
  preferences: 3,
  adventure: 4,
  budget: 5,
  revealed: 6,
  surprise: 6,
  demo_complete: 6,
}

interface BucketAgentProps {
  readonly stage: BucketStageId
  readonly eyebrow: string
  readonly title: string
  readonly body: string
  readonly customerAnswer: string | null
  readonly headingRef: RefObject<HTMLHeadingElement | null>
}

export function BucketAgent({
  stage,
  eyebrow,
  title,
  body,
  customerAnswer,
  headingRef,
}: BucketAgentProps) {
  const reduceMotion = useReducedMotion()

  return (
    <aside
      className="bucket-agent bucket-agent--staff"
      aria-label="Sub Zero team bucket guide"
    >
      <div className="bucket-agent__portrait-frame" aria-hidden="true">
        <img
          className="bucket-agent__portrait bucket-agent__portrait--staff"
          src="/brand/sub-zero-logo-primary.png"
          alt=""
        />
      </div>
      <div className="bucket-agent__dialogue">
        <div className="bucket-agent__identity">
          <strong>Sub Zero team · Bucket guide</strong>
          <span>Interactive preview</span>
        </div>
        <div
          className="bucket-agent__copy"
          data-treat-show-guide
        >
          <div className="bucket-agent__meta">
            <motion.p
              className="bucket-agent__eyebrow"
              key={`${stage}-eyebrow`}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                reduceMotion
                  ? { duration: motionTokens.reducedOpacity }
                  : {
                      duration: motionTokens.standard,
                      ease: [0.22, 1, 0.36, 1],
                    }
              }
            >
              {eyebrow}
            </motion.p>
            <span>Step {STAGE_PROGRESS[stage]} of 6</span>
          </div>
          <h2 ref={headingRef} tabIndex={-1}>
            {title}
          </h2>
          <motion.p
            key={`${stage}-body`}
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, y: 7, filter: 'blur(3px)' }
            }
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={
              reduceMotion
                ? { duration: motionTokens.reducedOpacity }
                : {
                    duration: motionTokens.standard,
                    ease: [0.22, 1, 0.36, 1],
                  }
            }
          >
            {body}
          </motion.p>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          {customerAnswer ? (
            <motion.div
              className="bucket-agent__customer-answer"
              key={customerAnswer}
              initial={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, x: 12, y: 4 }
              }
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, x: 8, y: -3 }
              }
              transition={
                reduceMotion
                  ? { duration: motionTokens.reducedOpacity }
                  : {
                      duration: motionTokens.standard,
                      ease: [0.22, 1, 0.36, 1],
                    }
              }
            >
              <span aria-hidden="true">Your last answer</span>
              <p>
                <span className="sr-only">Your last answer: </span>
                {customerAnswer}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </aside>
  )
}
